// Mirrors pi-poc/src/protocol.ts (Cadence spec #132); keep the two in sync

export const MAX_TEXT_CHARS = 20_000;

export type ThinkingLevel =
  | 'off'
  | 'minimal'
  | 'low'
  | 'medium'
  | 'high'
  | 'xhigh'
  | 'max';
export interface ModelRef {
  provider: string;
  id: string;
}
export interface ModelInfo extends ModelRef {
  name: string;
  thinkingLevels: ThinkingLevel[];
}
export interface SessionContext {
  targetType?:
    | 'dashboard'
    | 'homepage'
    | 'chart_group'
    | 'workflow_step'
    | 'business_app';
  dashboardId?: string;
  dashletId?: string;
  objectId?: string;
  automationId?: string;
  stepId?: string;
  appApiName?: string;
}
export interface UserLocation {
  path: string;
}
export interface Usage {
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite: number;
  totalTokens: number;
  costTotal: number;
}
export interface SourceFile {
  path: string;
  content: string;
}
export interface SnapshotRef {
  id: string;
  createdAt: string; // ISO 8601
  reason: 'push' | 'code_step' | 'workspace';
  dashletId: string | null; // null for code_step versions
  filesUrl?: string; // presigned GET of the Snapshot JSON; absolute or relative to the ws URL's http(s) origin
}
export interface Snapshot extends Omit<SnapshotRef, 'filesUrl'> {
  files: SourceFile[];
  blobs?: { path: string; sha256: string; size: number }[];
}
/** A tool's own human-readable rendering of its result; rows without one show `summary`. */
export interface ToolDisplay {
  /** One line, <= MAX_TOOL_DISPLAY_TITLE_CHARS. */
  title: string;
  /** Plain text with real newlines, <= MAX_TOOL_DISPLAY_BODY_CHARS. */
  body?: string;
}
export interface StoredCapture {
  path: string;
  width: number;
  height: number;
}
export type HistoryItem =
  | { kind: 'user'; text: string }
  | { kind: 'assistant'; text: string }
  | {
      kind: 'tool';
      toolName: string;
      isError: boolean;
      summary: string;
      toolCallId?: string;
      capture?: StoredCapture;
      label?: string;
      display?: ToolDisplay;
    };
export interface PushInfo {
  action: 'created' | 'updated';
  dashboardId: string;
  dashletId: string;
  url: string;
}
export interface SessionCredentials {
  apiKey: string;
  userId: string;
  businessId: string;
  expires: string | number;
}
export const INTERACTIVE_CAPABILITY = 'interactive';
export const CAPTURE_CAPABILITY = 'capture';
export const NAVIGATE_CAPABILITY = 'navigate';
export const REFRESH_PLUGINS_CAPABILITY = 'refresh_plugins';
export const APPROVAL_CAPABILITY = 'approval';
export type ClientCapability =
  | typeof APPROVAL_CAPABILITY
  | typeof CAPTURE_CAPABILITY
  | typeof INTERACTIVE_CAPABILITY
  | typeof NAVIGATE_CAPABILITY
  | typeof REFRESH_PLUGINS_CAPABILITY;
export type CaptureFailureCode =
  | 'not_rendered'
  | 'minimized'
  | 'timeout'
  | 'capture_failed'
  | 'unsupported'
  | 'too_large';
export interface CaptureUploadTarget {
  url: string;
  method: 'PUT';
  headers: Record<string, string>;
  maxBytes: number;
}
export interface CaptureUpload {
  png: CaptureUploadTarget;
  failure: CaptureUploadTarget;
  expiresAt: number; // unix ms
}
export type UiCaptureTarget =
  | { kind: 'floating_frame'; frameApiName: string }
  | { kind: 'page' };
export interface CaptureFailureBody {
  code: CaptureFailureCode;
  message?: string;
}
export interface ActionCodeStepSecret {
  id?: string;
  name: string;
}
/** An input's source, set by `input_type`: only that one key is present. */
export interface ActionCodeStepInput {
  input_type: 'variable' | 'field' | 'related_field' | 'static_value';
  name: string;
  data_type: string;
  is_list: boolean;
  variable?: { name: string };
  field?: { id: string };
  related_field?: { id: string };
  /** `{ value }`; employee: `{ employee_id }`; entity: `{ custom_object: { id }, entity_record_id }`. */
  static_value?: {
    value?: unknown;
    employee_id?: string | null;
    custom_object?: { id: string };
    entity_record_id?: string | null;
  };
}
export interface ActionCodeStepOutput {
  output_type: 'variable' | 'field' | 'related_field';
  name: string;
  data_type: string;
  is_list: boolean;
  conflict_resolution: string;
  create_missing_option: boolean;
  variable?: { name: string };
  field?: { id: string };
  related_field?: { id: string };
}
export interface ActionCodeStepWrite {
  runtime: string;
  script: string;
  secrets: ActionCodeStepSecret[];
  inputs: ActionCodeStepInput[];
  outputs: ActionCodeStepOutput[];
}
export interface ResultUpload {
  result: CaptureUploadTarget;
  expiresAt: number; // unix ms
}
export type CodeStepSaveFailureCode =
  | 'not_open'
  | 'step_missing'
  | 'dirty'
  | 'conflict'
  | 'rejected'
  | 'forbidden'
  | 'unsupported'
  | 'failed';
export type CodeStepSaveResult =
  | { ok: true; revision?: number }
  | {
      ok: false;
      code: CodeStepSaveFailureCode;
      message?: string;
      errors?: string[];
    };
export const MAX_CODE_STEP_SAVE_RESULT_BYTES = 16 * 1024;
export const MAX_CODE_STEP_SAVE_MESSAGE_CHARS = 300;
export const MAX_CODE_STEP_DESCRIPTION_CHARS = 200;
export const MAX_CODE_STEP_SAVE_ERRORS = 20;
export type NavigateFailureCode = 'declined' | 'busy' | 'unavailable';
export type NavigateResult =
  | { ok: true; path: string }
  | { ok: false; code: NavigateFailureCode; message?: string };
export type RefreshPluginsResult =
  | { ok: true }
  | { ok: false; code: 'unavailable'; message?: string };
export type OpenFloatingFrameResult =
  | { ok: true }
  | { ok: false; code: 'not_found' | 'hidden' | 'unavailable' };
/** One kizen plan operation; `action` is kizen's (create|update|delete|duplicate|upsert|apply|skip, or newer). */
export const APPROVAL_EFFECTS = [
  'deletes',
  'archives',
  'removes_roles',
  'replaces_options',
  'removes_values',
  'service_account',
  'unrecognized',
] as const;
/** What a destructive operation removes or changes; sent only when `destructive`. */
export type ApprovalEffect = (typeof APPROVAL_EFFECTS)[number];
export const isApprovalEffect = (v: unknown): v is ApprovalEffect =>
  (APPROVAL_EFFECTS as readonly unknown[]).includes(v);
export interface ApprovalOperation {
  action: string;
  kind: string;
  key: string;
  destructive: boolean;
  /** Server-truncated JSON text of kizen's preview. */
  detail?: string;
  effect?: ApprovalEffect;
  /** Human-readable name of the target; one line, <= MAX_APPROVAL_LABEL_CHARS. */
  label?: string;
  /** The target's own api name (one identifier), only when it has no label; <= MAX_APPROVAL_LABEL_CHARS. */
  apiName?: string;
  /** Human-readable name of the target's parent (e.g. a field's object); <= MAX_APPROVAL_LABEL_CHARS. */
  parentLabel?: string;
  /** The parent's api name, when it has no label; <= MAX_APPROVAL_LABEL_CHARS. */
  parentKey?: string;
  /** Short secondary detail, e.g. a field's type; <= MAX_APPROVAL_TYPE_LABEL_CHARS. */
  typeLabel?: string;
}
export const MAX_APPROVAL_LABEL_CHARS = 120;
export const MAX_APPROVAL_TYPE_LABEL_CHARS = 60;
export interface ApprovalPlan {
  planId: string;
  summary: string;
  /** May be truncated by the server; destructive operations are always included. */
  operations: ApprovalOperation[];
  /** Counts over every operation in the plan, including any truncated ones. */
  counts: { total: number; destructive: number };
}
export type ApprovalRequest =
  | { kind: 'plans'; plans: ApprovalPlan[] }
  | { kind: 'command'; argv: string[] };
export type ApprovalGrant = 'once' | 'window';
export type ApprovalFailureCode = 'declined' | 'busy' | 'unavailable';
export const MAX_APPROVAL_MESSAGE_CHARS = 300;
/** Never `grant: 'window'` when the request's `allowWindow` is false. */
export type ApprovalResult =
  | { ok: true; grant: ApprovalGrant }
  | { ok: false; code: ApprovalFailureCode; message?: string };
export const MAX_TOOL_DISPLAY_TITLE_CHARS = 200;
export const MAX_TOOL_DISPLAY_BODY_CHARS = 4000;
const clip = (value: string, max: number) =>
  value.length > max ? `${value.slice(0, max)}…` : value;
/**
 * Total guard over an untrusted `display`: undefined (never throws) unless the
 * title is a non-blank string. A non-string or blank body is dropped. Both are
 * clipped to the contract's limits, and the title is folded onto one line.
 */
export const parseToolDisplay = (v: unknown): ToolDisplay | undefined => {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return undefined;
  const { title, body } = v as Record<string, unknown>;
  if (typeof title !== 'string') return undefined;
  const line = title.replace(/\s+/g, ' ').trim();
  if (!line) return undefined;
  const display: ToolDisplay = {
    title: clip(line, MAX_TOOL_DISPLAY_TITLE_CHARS),
  };
  return typeof body === 'string' && body.trim()
    ? { ...display, body: clip(body, MAX_TOOL_DISPLAY_BODY_CHARS) }
    : display;
};
const MAX_LOCATION_PATH_CHARS = 1024;
const MAX_API_NAME_CHARS = 100;
const API_NAME_RE = new RegExp(
  `^[A-Za-z0-9][A-Za-z0-9_]{0,${MAX_API_NAME_CHARS - 1}}$`
);
export const isApiName = (v: unknown): v is string =>
  typeof v === 'string' && API_NAME_RE.test(v);
const UNSAFE_CHARS_RE = /[\p{Cc}\p{Cf}\p{Zl}\p{Zp}]/u;
export const isLocationPath = (v: unknown): v is string =>
  typeof v === 'string' &&
  v.startsWith('/') &&
  v.length <= MAX_LOCATION_PATH_CHARS &&
  !UNSAFE_CHARS_RE.test(v);
export const isNavigatePath = (v: unknown): v is string =>
  isLocationPath(v) && !v.startsWith('//') && !v.includes('\\');
/**
 * How a start picks its conversation: resume `conversationId`, start fresh with `newConversation`,
 * or fork a copy of the `forkFrom` conversation (a duplicated block) under a new id.
 */
export type SessionStartConversation =
  | { conversationId?: string; newConversation?: never; forkFrom?: never }
  | { conversationId?: never; newConversation?: true; forkFrom?: never }
  | { conversationId?: never; newConversation?: never; forkFrom?: string };

export type ClientMessage =
  | ({
      type: 'session.start';
      context: SessionContext;
      model?: ModelRef;
      thinkingLevel?: ThinkingLevel;
      capabilities?: ClientCapability[];
      credentials: SessionCredentials;
      profile?: string;
    } & (
      SessionStartConversation
    ))
  | {
      type: 'snapshot.restore';
      snapshotId: string;
      credentials?: SessionCredentials;
    }
  | {
      type: 'user.message';
      text: string;
      credentials: SessionCredentials;
      clientMessageId: string;
      location?: UserLocation;
    }
  | { type: 'session.abort' };
export type ServerMessage =
  | {
      type: 'session.ready';
      sessionId: string;
      models: ModelInfo[];
      model: ModelRef;
      thinkingLevel: ThinkingLevel;
      context: SessionContext;
      limits: { maxMs: number; idleMs: number };
      conversationId: string;
      resumed: boolean;
      turnActive: boolean;
      history: HistoryItem[];
      snapshots?: SnapshotRef[];
      snapshotPointer?: number;
    }
  | { type: 'assistant.delta'; kind: 'text' | 'thinking'; delta: string }
  | {
      type: 'assistant.message';
      text: string;
      usage?: Usage;
      stopReason: string;
    }
  | {
      type: 'tool.generating';
      toolCallId: string;
      toolName: string;
      argsSummary?: string;
      chars: number;
      label?: string;
    }
  | {
      type: 'tool.start';
      toolCallId: string;
      toolName: string;
      args: unknown;
      label?: string;
    }
  | { type: 'tool.update'; toolCallId: string; partial: string }
  | {
      type: 'tool.end';
      toolCallId: string;
      toolName: string;
      isError: boolean;
      summary: string;
      capture?: StoredCapture;
      display?: ToolDisplay;
    }
  | { type: 'turn.settled' }
  | {
      type: 'push.result';
      action: 'created' | 'updated';
      dashboardId: string;
      dashletId: string;
      url: string;
      conversationId: string;
      snapshot: SnapshotRef;
    }
  | {
      type: 'snapshot.restored';
      snapshotId: string;
      /** Block restores only (always present for reason 'push'). */
      push?: PushInfo;
      filesUrl: string;
    }
  | ErrorFrame
  | { type: 'message.accepted'; clientMessageId: string }
  | {
      type: 'block.capture';
      requestId: string;
      dashletId: string;
      toolCallId?: string;
      upload: CaptureUpload;
    }
  | { type: 'version.added'; snapshot: SnapshotRef }
  | {
      type: 'code_step.save';
      requestId: string;
      toolCallId?: string;
      automationId: string;
      stepId: string;
      description?: string;
      actionCodeStep: ActionCodeStepWrite;
      upload: ResultUpload;
    }
  | {
      type: 'ui.navigate';
      requestId: string;
      toolCallId: string;
      path: string;
      upload: ResultUpload;
    }
  | {
      type: 'ui.refresh_plugins';
      requestId: string;
      toolCallId: string;
      upload: ResultUpload;
    }
  | {
      type: 'ui.open_floating_frame';
      requestId: string;
      toolCallId: string;
      frameApiName: string;
      upload: ResultUpload;
    }
  | {
      type: 'ui.approval';
      requestId: string;
      toolCallId: string;
      upload: ResultUpload;
      allowWindow: boolean;
      windowMinutes: number;
      request: ApprovalRequest;
    }
  | {
      type: 'ui.capture';
      requestId: string;
      toolCallId: string;
      target: UiCaptureTarget;
      fullPage?: true;
      upload: CaptureUpload;
    }
  | { type: 'business_app.created'; appApiName: string }
  | { type: 'session.closed'; reason: SessionClosedReason };

export const SESSION_CLOSED_REASONS = [
  'client',
  'timeout_max',
  'timeout_idle',
  'auth',
  'server_error',
  'capacity',
  'conflict',
  'not_found',
  'forbidden',
  'fork_refused',
] as const;
export type SessionClosedReason = (typeof SESSION_CLOSED_REASONS)[number];

export const ERROR_CODES = [
  'bad_message',
  'not_ready',
  'already_started',
  'invalid_context',
  'bad_request',
  'auth',
  'not_found',
  /**
   * conversationId may not be continued by this caller, or the caller lacks the edit/push right on
   * its resource (checked at each turn start). Closes 'forbidden'. Echoes on a user.message.
   */
  'forbidden',
  'conflict',
  'session_create_failed',
  'conversation_invalid',
  /**
   * Model unavailable. At session start: closes 'server_error'. On the webapp backend a message
   * whose lease did not vend the session's model is refused with it (echoes; the socket stays open).
   */
  'model_unavailable',
  'busy',
  'restore_busy',
  'state_unavailable',
  'closed',
  'cancelled',
  'persist_failed',
  'files_skipped',
  'prompt_failed',
  'abort_failed',
  'unsupported',
  'unknown_snapshot',
  'restore_failed',
  'restore_note_failed',
  'kizen_ai_unavailable',
  /** A `forkFrom` start was refused: the source conversation can't be copied. Closes the session. */
  'fork_refused',
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

export const AUTH_REASONS = [
  'missing',
  'expired',
  'rejected',
  'unverified',
] as const;
export const BAD_REQUEST_REASONS = [
  'unknown_profile',
  'missing_required_pack',
  'invalid_conversation_id',
  'context_rejected',
  'profile_mismatch',
  'binding_mismatch',
  'new_conversation_with_id',
  'fork_with_conversation',
] as const;
export const BUSY_REASONS = [
  /** Another tab or a runTurn caller holds the conversation. */
  'conversation_locked',
  'save_pending',
  /** Another session holds the resource (code step, block, app) this conversation edits. */
  'resource_locked',
] as const;
export const STATE_UNAVAILABLE_REASONS = [
  'lock_unavailable',
  'save_pending',
  'head_check_failed',
  'lease_lost',
] as const;
export const KIZEN_AI_UNAVAILABLE_REASONS = [
  'permission',
  'feature_disabled',
  'billing',
] as const;
export const PERSIST_FAILURES = ['turn', 'version', 'timeout'] as const;
export const RESTORE_FAILURES = [
  'no_dashlet',
  'workspace',
  'push',
  'internal',
  'target_changed',
  'target_busy',
  'unknown_outcome',
  'unrestorable',
  'apply',
] as const;
export const SKIPPED_FILE_REASONS = [
  'too_large',
  'not_utf8',
  'too_many',
  'secret',
] as const;
export const CONTEXT_ID_FIELDS = [
  'dashboardId',
  'dashletId',
  'objectId',
  'automationId',
  'stepId',
  'appApiName',
] as const;

export type AuthReason = (typeof AUTH_REASONS)[number];
export type BadRequestReason = (typeof BAD_REQUEST_REASONS)[number];
export type BusyReason = (typeof BUSY_REASONS)[number];
export type StateUnavailableReason = (typeof STATE_UNAVAILABLE_REASONS)[number];
export type KizenAiUnavailableReason =
  (typeof KIZEN_AI_UNAVAILABLE_REASONS)[number];
export type PersistFailure = (typeof PERSIST_FAILURES)[number];
export type RestoreFailure = (typeof RESTORE_FAILURES)[number];
export type SkippedFileReason = (typeof SKIPPED_FILE_REASONS)[number];
export type ContextIdField = (typeof CONTEXT_ID_FIELDS)[number];

export interface ErrorParams {
  auth: { reason: AuthReason };
  bad_request: { reason: BadRequestReason };
  invalid_context: { fields: ContextIdField[] };
  busy: { reason: BusyReason };
  state_unavailable: { reason: StateUnavailableReason };
  persist_failed: { what: PersistFailure };
  files_skipped: {
    files: { path: string; reason: SkippedFileReason }[];
    more: number;
  };
  restore_failed: { reason: RestoreFailure };
  kizen_ai_unavailable: { reason: KizenAiUnavailableReason };
}
export type ErrorCodeWithParams = keyof ErrorParams;
type ErrorFrameOf<C extends ErrorCode> = {
  type: 'error';
  code: C;
  clientMessageId?: string;
} & (C extends ErrorCodeWithParams
  ? { params: ErrorParams[C] }
  : { params?: never });
export type ErrorFrame = { [C in ErrorCode]: ErrorFrameOf<C> }[ErrorCode];
